import {isInternetReachableSelector} from '../../redux/selectors/httpWrapper/netInfo';
import {netInfoSlice} from '../../redux/reducers/httpWrapper/netInfo';
import {Platform} from 'react-native';
import NetInfo, {NetInfoState, NetInfoSubscription} from '@react-native-community/netinfo';
import axios, {AxiosRequestConfig, Canceler} from 'axios';

import {store} from '../../redux';
import {blockingCallCountSlice} from '../../redux/reducers/httpWrapper/blockingCallCount';
import {storage, StorageKeys} from '../storage';
import {HttpWrapperOptions} from './options';
import {HttpStatusCode} from './httpStatusCode';
import {mocks} from './mock';
import {sleep} from '../pipes';
import {addNotification} from '../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../components/snackbar/model';
import {endSession} from '../../redux/actions/profile';
import {getRequestPromise} from './request';
import {loaderTextSlice} from '../../redux/reducers/loader/loaderText';
import {getHttpErrorMessage} from '../utils/getHttpErrorMessage';

const CancelToken = axios.CancelToken;
type RequestType = 'get' | 'post' | 'put' | 'delete';

type Overwrite<T, U> = Pick<T, Exclude<keyof T, keyof U>> & U;
export type AxiosRequestConfigExt = Overwrite<AxiosRequestConfig, {method: RequestType}>;

type QueryString = {_t?: string};

const cancelRequestBecauseOfNewOne = 'cancel request because of new one';
const sleepMs = 0;
const withMocks = false;

const urlToMockName = (url: string, method: RequestType): string => {
  const prefix = '/API/1_0/';
  const prefixLen = prefix.length;
  let baseUrl = url.split('?')[0];
  let pathParts = baseUrl.substring(baseUrl.indexOf(prefix) + prefixLen).split('/');
  const result = pathParts.reduce((state, item) => {
    if (!isNaN(+item)) {
      return `${state}_Number`;
    }
    return `${state}_${item}`;
  }, `${method}`);
  return result;
  // const matchMethodRegex = /API\/1_0\/(\w*)[/]{0,1}/;
  // return `${method}${url.match(matchMethodRegex)?.[1]}`;
};

class InternalError {
  constructor(public response: {data: any}) {
    // do nothing
  }
}
class HttpError extends Error {
  constructor(public response: {data: any}) {
    super();

    let data = response?.data;

    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        // do nothing
      }
    }

    this.message =
      (Array.isArray(data) ? data.map((item: {id: number; message: string}) => item.message).join('\n') : null) ||
      data?.message;
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Headers {
  [key: string]: string;
}

export type InternetStatusListener = (value: boolean) => void;

export class HttpService {
  private cancelations: {[key: string]: Canceler} = {};
  private unsubscribeNetInfoEventListener: NetInfoSubscription;
  private netInfoStateDetection: Promise<NetInfoState>;

  public showFanCounter = 0;

  constructor() {
    if (Platform.OS === 'web' && (NetInfo as any)?.configure) {
      (NetInfo as any).configure({
        reachabilityShouldRun: () => false,
      });
    }

    const netInfoEventListener = (state: NetInfoState): NetInfoState => {
      store.dispatch(netInfoSlice.actions.setState(state));
      return state;
    };

    // Wrap NetInfo calls to handle AbortError on web
    const initNetInfo = async (): Promise<NetInfoState> => {
      try {
        const state = await NetInfo.fetch();
        return netInfoEventListener(state);
      } catch (error: any) {
        // Ignore AbortError from NetInfo on web - this is expected behavior
        if (error?.name !== 'AbortError') {
          console.warn('NetInfo fetch error:', error);
        }
        return {isConnected: true, isInternetReachable: true} as NetInfoState;
      }
    };

    this.netInfoStateDetection = initNetInfo();

    // Add error handler for unhandled AbortError from NetInfo's internal listeners
    if (Platform.OS === 'web') {
      const originalHandler = window.onunhandledrejection;
      window.onunhandledrejection = (event: PromiseRejectionEvent) => {
        if (event.reason?.name === 'AbortError' && event.reason?.message?.includes('signal is aborted')) {
          event.preventDefault();
          return;
        }
        if (originalHandler) {
          return originalHandler.call(window, event);
        }
      };
    }

    this.unsubscribeNetInfoEventListener = NetInfo.addEventListener(netInfoEventListener);
  }

  public cleanup: () => void = () => {
    if (this.unsubscribeNetInfoEventListener) {
      this.unsubscribeNetInfoEventListener();
    }
  };

  public get<T>(url: string, httpWrapperOptions?: HttpWrapperOptions): Promise<T> {
    const queryStringParams: QueryString = {};
    if (httpWrapperOptions?.cacheQueryParams) {
      queryStringParams._t = httpWrapperOptions.cacheQueryParams;
    }
    return this.processRequest<T>(url, 'get', null, httpWrapperOptions, queryStringParams);
  }

  public post<T = void>(url: string, data?: any, httpWrapperOptions?: HttpWrapperOptions): Promise<T> {
    return this.processRequest<T>(url, 'post', data, httpWrapperOptions);
  }

  public put<T = void>(url: string, data?: any, httpWrapperOptions?: HttpWrapperOptions): Promise<T> {
    return this.processRequest<T>(url, 'put', data, httpWrapperOptions);
  }

  public delete<T = void>(url: string, data?: any, httpWrapperOptions?: HttpWrapperOptions): Promise<T> {
    return this.processRequest<T>(url, 'delete', data, httpWrapperOptions);
  }

  private processRequest<T>(
    url: string,
    method: RequestType,
    data?: any,
    httpWrapperOptions = new HttpWrapperOptions(),
    queryParams: any = null,
  ): Promise<T> {
    const result = new Promise<T>((resolve, reject) => {
      (async () => {
        await this.netInfoStateDetection;
        if (httpWrapperOptions.trackRequest) {
          store.dispatch(netInfoSlice.actions.setError(false));
        }

        if (httpWrapperOptions.text) {
          store.dispatch(loaderTextSlice.actions.setText(httpWrapperOptions.text));
        }

        const isInternetReachable = isInternetReachableSelector()(store.getState());

        let waitTimeout: ReturnType<typeof setTimeout> | null = null;
        if (httpWrapperOptions.wait) {
          waitTimeout = setTimeout(() => {
            store.dispatch(blockingCallCountSlice.actions.increment());
          }, httpWrapperOptions.waitTimeout);
        }

        const token = await storage.getItem<string>(StorageKeys.token);
        const headers: any = httpWrapperOptions.headers || {};
        if (token && headers.Authorization == null) {
          headers.Authorization = `Bearer ${token}`;
        }

        if (sleepMs) {
          await sleep(sleepMs);
        }

        if (withMocks) {
          const methodName = urlToMockName(url, method) as keyof typeof mocks;
          if (mocks[methodName]) {
            const responseData =
              typeof mocks[methodName] === 'function' ? (mocks[methodName] as any)(data) : (mocks[methodName] as any);
            if (httpWrapperOptions.wait) {
              store.dispatch(blockingCallCountSlice.actions.decrement());
              if (waitTimeout) {
                clearTimeout(waitTimeout);
              }
            }

            return resolve(responseData);
          } else {
            console.warn(`mock ${methodName} for method ${method} ${url} not found`);
          }
        }

        const axiosRequestConfig: AxiosRequestConfigExt = Object.assign(
          {},
          {
            method,
            withCredentials: Platform.OS !== 'web', // TODO: uncomment it later
            responseType: httpWrapperOptions.responseType,
            timeout: httpWrapperOptions.timeout,
            headers,
            data: data ? data : undefined,
            params: queryParams,
          },
        );

        const normalizedUrl = this.normalizeUrl(url);
        try {
          if (!isInternetReachable) {
            throw new Error('Нет доступа к интернету');
          }

          let res: any = null;
          let callCounter = 0;
          while (true) {
            try {
              if (httpWrapperOptions.requestSequenceIdentificator) {
                if (this.cancelations[httpWrapperOptions.requestSequenceIdentificator]) {
                  this.cancelations[httpWrapperOptions.requestSequenceIdentificator](cancelRequestBecauseOfNewOne);
                  delete this.cancelations[httpWrapperOptions.requestSequenceIdentificator];
                }
                axiosRequestConfig.cancelToken = new CancelToken((c) => {
                  if (!httpWrapperOptions.requestSequenceIdentificator) return;
                  this.cancelations[httpWrapperOptions.requestSequenceIdentificator] = c;
                });
              }
              res = await getRequestPromise(normalizedUrl, axiosRequestConfig);

              const status = res.status ?? res.respInfo?.status;

              if (status === HttpStatusCode.accepted) {
                throw new InternalError(res);
              }
              if (status >= HttpStatusCode.badRequest) {
                throw new HttpError(res);
              }
              if (httpWrapperOptions.requestSequenceIdentificator) {
                delete this.cancelations[httpWrapperOptions.requestSequenceIdentificator];
              }
              break;
            } catch (err: any) {
              if (err.message === cancelRequestBecauseOfNewOne) {
                // ToDo: не понятная фича: как будто стреляем в ногу себе
                // Надо бы переделать как сейчас в wastehub: первый вызову принимают результат финального
                res = {
                  data: null,
                };
                console.warn(`Request "${url}" canceled by next one`);

                break;
              }

              ++callCounter;
              if (httpWrapperOptions.requestSequenceIdentificator) {
                if (this.cancelations[httpWrapperOptions.requestSequenceIdentificator]) {
                  // console.warn('request canceled by next try');
                  this.cancelations[httpWrapperOptions.requestSequenceIdentificator]();
                  delete this.cancelations[httpWrapperOptions.requestSequenceIdentificator];
                }
              }

              if (err.code !== 'ECONNABORTED' || callCounter > httpWrapperOptions.extraTries) {
                throw err;
              }
            }
          }
          // this.showDialog(httpWrapperOptions, true, undefined);

          // if (
          //   res &&
          //   res.headers &&
          //   res.headers['web-client-version'] &&
          //   state.resources.settings &&
          //   res.headers['web-client-version'] !== state.resources.settings.webClientVersion &&
          //   new Date().getTime() - initializeTimestamp > minReloadingIntervalMs
          // ) {
          //   setTimeout(() => location.reload(true), reloadAfterMs);
          // }
          // if (res && res.headers && res.headers['resource-version']) {
          //   store.dispatch(checkVersion(+res.headers['resource-version']));
          // }
          resolve(res.data);
        } catch (reason: any) {
          if (httpWrapperOptions.trackRequest) {
            store.dispatch(netInfoSlice.actions.setError(true));
          }
          if (httpWrapperOptions.errors) {
            let status = reason.response && reason.response.status ? reason.response.status : HttpStatusCode.exception;

            if (reason.code === 'ECONNABORTED') {
              status = HttpStatusCode.timeout;
            }

            if (status === HttpStatusCode.unauthorized) {
              store.dispatch<any>(endSession());
              return;
              // ToDo: window.location.href = '/';
            }

            if (isInternetReachable) {
              // todo: потестить с реальным api, возможно еще откуда-то можно брать сообщения
              let message = getHttpErrorMessage(reason, '');

              if (!message) {
                if (status === HttpStatusCode.timeout) {
                  message = 'Превышено время ответа от сервера';
                } else if (reason?.message === 'Network Error') {
                  message = 'Ошибка при обращении к серверу';
                } else {
                  message = 'Ошибка сети';
                }
              }

              const ignoreDisplayError =
                typeof httpWrapperOptions.ignoreDisplayError === 'function'
                  ? httpWrapperOptions.ignoreDisplayError(message)
                  : httpWrapperOptions.ignoreDisplayError;

              if (message && !ignoreDisplayError) {
                this.displayError(message);
              }
            }
          }
          if (httpWrapperOptions.throwException) {
            if (reason?.message === 'Network Error') {
              reject(new Error('Ошибка сети'));
            } else {
              reject(reason.response || reason);
            }
          } else {
            resolve(undefined as any);
          }
        } finally {
          if (httpWrapperOptions.wait) {
            store.dispatch(blockingCallCountSlice.actions.decrement());
            if (waitTimeout) {
              clearTimeout(waitTimeout);
            }
          }
        }
      })();
    });

    return result;
  }

  private displayError(message: string) {
    store.dispatch(
      addNotification({
        message,
        type: SnackbarNotificationType.Error,
      }) as any,
    );
  }

  private normalizeUrl(url: string): string {
    if (!url.startsWith('http')) {
      return `${process.env.REACT_APP_BACKEND_URL ?? ''}${url}`;
    } else {
      return `${url}`;
    }
  }
}
